import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { 
  CreditCard, Settings, CheckCircle, 
  AlertCircle, Loader2, Save, ShieldCheck
} from 'lucide-react';
import api from '../utils/api';

export default function MercadoPagoConfig() {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  
  const [config, setConfig] = useState({
    mercado_pago_token: '',
    mercado_pago_configured: false,
    deposit_enabled: false,
    deposit_type: 'fixed',
    deposit_value: 0
  });

  useEffect(() => {
    loadConfig();
  }, [id]);

  const loadConfig = async () => {
    try {
      const response = await api.get(`/config/mercadopago`, { params: { id } });
      setConfig(prev => ({
        ...prev,
        mercado_pago_token: response.data.mercado_pago_token || '',
        mercado_pago_configured: !!response.data.mercado_pago_configured,
        deposit_enabled: !!response.data.deposit_enabled,
        deposit_type: response.data.deposit_type || 'fixed',
        deposit_value: response.data.deposit_value || 0
      }));
    } catch (err) {
      console.error('Erro ao carregar configurações:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = await api.put(`/config/mercadopago`, 
        {
          mercado_pago_token: config.mercado_pago_token,
          deposit_enabled: config.deposit_enabled,
          deposit_type: config.deposit_type,
          deposit_value: Number(config.deposit_value || 0)
        },
        { params: { id } }
      );

      setSuccess(response.data.message);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao salvar configurações');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="card">
        <div className="p-6 border-b border-dark-600">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-primary-400" />
            Configurações do Mercado Pago
          </h2>
          <p className="text-gray-400 mt-1">
            Configure a API do Mercado Pago para gerar pagamentos PIX
          </p>
        </div>

        <form onSubmit={handleSave} className="p-6 space-y-6">
          {/* Credenciais Mercado Pago */}
          <div>
            <h3 className="text-white font-medium mb-4 flex items-center gap-2">
              <Settings className="w-4 h-4 text-primary-400" />
              Credenciais da API
            </h3>
            
            <div className="grid sm:grid-cols-1 gap-4">
              <div>
                <label className="label">Access Token</label>
                <input
                  type="text"
                  placeholder={config.mercado_pago_configured ? 'Token ja configurado. Preencha apenas se quiser trocar.' : 'Token de acesso do Mercado Pago'}
                  className="input"
                  value={config.mercado_pago_token}
                  onChange={(e) => setConfig({...config, mercado_pago_token: e.target.value})}
                />
                <p className="text-xs text-gray-500 mt-1">
                  {config.mercado_pago_configured
                    ? 'Por seguranca, o token salvo nao e exibido no painel.'
                    : 'Obtido em https://www.mercadopago.com.br/developers'}
                </p>
              </div>
            </div>
          </div>

          <div className="border-t border-dark-600 pt-6">
            <h3 className="text-white font-medium mb-4">Sinal de reserva</h3>
            <button
              type="button"
              onClick={() => setConfig({ ...config, deposit_enabled: !config.deposit_enabled })}
              className={`w-full flex items-start justify-between gap-4 rounded-xl border p-5 text-left transition-all duration-200 overflow-hidden ${
                config.deposit_enabled
                  ? 'border-primary-500/50 bg-primary-500/10 shadow-lg shadow-primary-950/20'
                  : 'border-dark-700 bg-dark-800/60 hover:border-primary-500/30 hover:bg-dark-700/70'
              }`}
              aria-pressed={config.deposit_enabled}
            >
              <div className="flex items-start gap-4 min-w-0">
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${
                  config.deposit_enabled ? 'bg-primary-500/20 text-primary-300' : 'bg-dark-700 text-gray-400'
                }`}>
                  <ShieldCheck className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-white">Exigir sinal antes de confirmar</p>
                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${
                      config.deposit_enabled
                        ? 'text-green-300 border-green-500/30 bg-green-500/10'
                        : 'text-gray-400 border-dark-600 bg-dark-700/70'
                    }`}>
                      {config.deposit_enabled ? 'Ativo' : 'Desativado'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-400 mt-1">
                    Quando ativo, o cliente paga um sinal via Pix ao escolher pagar na barbearia.
                  </p>
                </div>
              </div>

              <span className={`relative mt-1 inline-flex h-6 w-12 flex-shrink-0 items-center rounded-full border transition-colors ${
                config.deposit_enabled
                  ? 'bg-primary-500 border-primary-400'
                  : 'bg-dark-700 border-dark-600'
              }`}>
                <span className={`inline-block h-5 w-5 rounded-full bg-white shadow-md transition-transform ${
                  config.deposit_enabled ? 'translate-x-6' : 'translate-x-0.5'
                }`} />
              </span>
            </button>

            {config.deposit_enabled && (
              <div className="grid sm:grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="label">Tipo de sinal</label>
                  <select
                    className="input"
                    value={config.deposit_type}
                    onChange={(e) => setConfig({ ...config, deposit_type: e.target.value })}
                  >
                    <option value="fixed">Valor fixo</option>
                    <option value="percentage">Percentual</option>
                  </select>
                </div>
                <div>
                  <label className="label">{config.deposit_type === 'percentage' ? 'Percentual (%)' : 'Valor (R$)'}</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="input"
                    value={config.deposit_value}
                    onChange={(e) => setConfig({ ...config, deposit_value: e.target.value })}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Mensagens de feedback */}
          {success && (
            <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20 flex items-center gap-2 text-green-400">
              <CheckCircle className="w-4 h-4" />
              {success}
            </div>
          )}

          {error && (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 flex items-center gap-2 text-red-400">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          )}

          {/* Botão */}
          <button
            type="submit"
            disabled={saving}
            className="btn-primary w-full"
          >
            {saving ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Salvando...
              </>
            ) : (
              <>
                <Save className="w-5 h-5 mr-2" />
                Salvar Configurações
              </>
            )}
          </button>
        </form>
      </div>

      {/* Guia de Configuração */}
      <div className="card">
        <div className="p-6">
          <h3 className="text-white font-medium mb-4 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-yellow-400" />
            Como Configurar
          </h3>
          
          <div className="space-y-3 text-sm text-gray-400">
            <div className="flex gap-3">
              <span className="text-primary-400 font-medium">1.</span>
              <p>Acesse <a href="https://www.mercadopago.com.br/developers" target="_blank" rel="noopener noreferrer" className="text-primary-400 hover:underline">Mercado Pago Developers</a></p>
            </div>
            <div className="flex gap-3">
              <span className="text-primary-400 font-medium">2.</span>
              <p>Crie um aplicativo ou use um existente</p>
            </div>
            <div className="flex gap-3">
              <span className="text-primary-400 font-medium">3.</span>
              <p>Vá em "Credenciais" e copie o Access Token</p>
            </div>
            <div className="flex gap-3">
              <span className="text-primary-400 font-medium">4.</span>
              <p>Cole o Access Token no campo acima e clique em "Salvar"</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
